import { useState } from "react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function LeadForm({ source = "homepage", productInterest = null, variant = "light", presetUseCase = "" }) {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const industryOptions = t.industries.cards.map((c) => c.title);
  const useCaseOptions = t.useCases.cards.map((c) => c.title);

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    industry: "", use_case: presetUseCase || "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.phone || !form.message) {
      toast.error(f.errEmpty);
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        ...form,
        source,
        product_interest: productInterest,
        language: lang,
      });
      setSuccess(true);
      toast.success(f.thankYou);
      setForm({ name: "", email: "", company: "", phone: "", industry: "", use_case: presetUseCase || "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || f.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const inputCls = isDark
    ? "w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
    : "w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/30 transition-all";
  const lblCls = `block text-xs font-mono-tech uppercase tracking-widest mb-2 ${isDark ? "text-white/60" : "text-slate-500"}`;

  if (success) {
    return (
      <div data-testid="lead-form-success" className={`${isDark ? "bg-white/5 border-white/15 text-white" : "bg-slate-50 border-slate-200 text-slate-900"} border p-10 text-center`}>
        <CheckCircle2 size={48} className="mx-auto text-blue-500 mb-4" />
        <h3 className="font-display text-2xl font-bold mb-2">{f.successTitle}</h3>
        <p className={isDark ? "text-white/70" : "text-slate-600"}>{f.successText}</p>
        <button
          data-testid="lead-form-send-another"
          onClick={() => setSuccess(false)}
          className="mt-6 text-sm uppercase tracking-widest font-semibold text-blue-500 hover:text-blue-600"
        >
          {f.successAnother}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="lead-form" className={`${isDark ? "bg-white/[0.03] border-white/15" : "bg-white border-slate-200"} border p-6 md:p-8`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={lblCls}>{f.name}</label>
          <input data-testid="lead-name" name="name" value={form.name} onChange={onChange} className={inputCls} />
        </div>
        <div>
          <label className={lblCls}>{f.email}</label>
          <input data-testid="lead-email" type="email" name="email" value={form.email} onChange={onChange} className={inputCls} />
        </div>
        <div>
          <label className={lblCls}>{f.company}</label>
          <input data-testid="lead-company" name="company" value={form.company} onChange={onChange} className={inputCls} />
        </div>
        <div>
          <label className={lblCls}>{f.phone}</label>
          <input data-testid="lead-phone" name="phone" value={form.phone} onChange={onChange} className={inputCls} />
        </div>
        <div>
          <label className={lblCls}>{f.industry}</label>
          <select data-testid="lead-industry" name="industry" value={form.industry} onChange={onChange} className={inputCls}>
            <option value="" style={{ color: "#0f172a", background: "#ffffff" }}>{f.industryPlaceholder}</option>
            {industryOptions.map((o) => (
              <option key={o} value={o} style={{ color: "#0f172a", background: "#ffffff" }}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={lblCls}>{f.useCase}</label>
          <select data-testid="lead-usecase" name="use_case" value={form.use_case} onChange={onChange} className={inputCls}>
            <option value="" style={{ color: "#0f172a", background: "#ffffff" }}>{f.usecasePlaceholder}</option>
            {useCaseOptions.map((o) => (
              <option key={o} value={o} style={{ color: "#0f172a", background: "#ffffff" }}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className={lblCls}>{f.message}</label>
        <textarea data-testid="lead-message" name="message" value={form.message} onChange={onChange} rows={4} className={inputCls} placeholder={f.placeholder} />
      </div>
      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className={`text-xs ${isDark ? "text-white/50" : "text-slate-500"}`}>{f.consent}</p>
        <button
          data-testid="lead-submit"
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {loading ? <><Loader2 size={16} className="animate-spin" /> {f.sending}</> : `${f.submit} →`}
        </button>
      </div>
    </form>
  );
}
